<script setup lang="ts">
// Confirmação de saque de bônus (Figma node 24352:44729 desktop /
// 24352:44566 mobile) — mesmo shell dos outros modais overlay do Minha
// Conta V2 (bottom sheet no mobile, card centralizado no desktop) e mesmo
// wiring por modalType do BonusRules.vue: os dois são abertos via
// toggleModal(<seu tipo>) (ver BonusWallet.vue) e o Global.vue os renderiza
// direto (v-else-if por modalType), sem embrulhar no <BaseModal> genérico —
// o shell (Teleport/backdrop/animação) é definido aqui dentro, não lá.
const { modalType, isModalOpen } = storeToRefs(useLayoutStore());
const { closeModal, openToaster } = useLayoutStore();
const { updateWallet, showMoneyFormat } = useWallet();
const { rollover, bonusWallet } = storeToRefs(useWalletStore());
const { parameters } = useParameters();
const { t } = useTexts();

const isOpen = computed(
  () => isModalOpen.value && modalType.value === 'bonus-withdraw',
);

const modalCardRef = ref<HTMLElement | null>(null);

const { trapFocus } = useModalA11y(modalCardRef, isOpen, closeModal);

const maxBonusWithdrawalMultiply = computed<string>(() => {
  if (!parameters.value?.qtd_withdrawal_bonus) {
    return '1x';
  }

  return `${parameters.value?.qtd_withdrawal_bonus}x`;
});

// ENG-863: o valor resgatável é o MENOR entre o saldo bônus atual e o teto
// que o core calcula a partir do bônus creditado
// (`max_bonus_withdraw_amount` = creditado x `qtd_withdrawal_bonus`). Se o
// saldo cresceu acima do teto, o lucro não é sacável; se caiu abaixo, não dá
// pra sacar mais do que existe na carteira.
//
// ATENÇÃO À UNIDADE: `bonusWallet.credit` já vem em reais da store
// (stores/wallet.ts divide por 100 na leitura da carteira), enquanto
// `rollover.max_bonus_withdraw_amount` chega CRU em centavos do
// `bonus/rollover`. O MIN só vale depois de normalizar os dois.
//
// Teto ausente/zerado = o core não informou o limite nesta resposta (rollover
// ainda não buscado, ou bônus sem a regra): sem teto conhecido cai no saldo
// atual, em vez de anunciar R$ 0,00 pra quem tem bônus em carteira (mesmo
// fallback do snapshot de carteira no ENG-846).
const withdrawableBonusAmount = computed<number>(() => {
  const currentBonusBalance = bonusWallet.value.credit || 0;
  const maxWithdrawAmount =
    (rollover.value?.max_bonus_withdraw_amount || 0) / 100;

  if (maxWithdrawAmount <= 0) {
    return currentBonusBalance;
  }

  return Math.min(currentBonusBalance, maxWithdrawAmount);
});

const isLoading = ref<boolean>(false);

function requestBonusWithdraw() {
  if (bonusWallet.value.credit <= 0) {
    return;
  }

  const { ApiService } = useApiService();

  isLoading.value = true;

  ApiService.post('/bonus/transfer', {})
    .then((res) => {
      openToaster({
        message: res.data.message,
        type: 'success',
      });
      closeModal();
      updateWallet();
    })
    .catch((err) => {
      const message = err?.data?.message
        ? err?.data?.message
        : err?.message
          ? err?.message
          : t('general.error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <div
        v-if="isOpen"
        class="user-wallet-bonus-withdraw-modal fixed inset-0 z-[1000] flex items-end justify-center font-sans lg:items-center lg:p-[16px]"
        @dragstart.prevent
      >
        <!-- o clique fora cai NESTE backdrop (cobre o wrapper inteiro) -->
        <div
          class="modal-backdrop absolute inset-0 bg-black/70"
          @click="closeModal"
        />

        <div
          ref="modalCardRef"
          aria-labelledby="bonus-withdraw-title"
          aria-modal="true"
          class="modal-card relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-[20px] bg-v2-default-200 text-left lg:max-w-[420px] lg:rounded-[20px]"
          role="dialog"
          tabindex="-1"
          @keydown="trapFocus"
        >
          <BaseLoading
            class="!bg-v2-default-200"
            :full-screen="false"
            :show="isLoading"
          />

          <UserV2ModalCloseButton @close="closeModal" />

          <!-- rola sozinho, sem o X junto: viewport baixo (ex.: paisagem
               mobile) não corta mais o topo do card, ver BonusRules.vue
               (mesmo split) / Modal.vue (.wrapper/.inner) pro mesmo motivo. -->
          <div
            class="flex min-h-0 flex-col gap-[14px] overflow-y-auto px-[16px] pb-[24px] pt-[16px]"
          >
            <p
              id="bonus-withdraw-title"
              class="pr-[40px] text-[15px] font-bold leading-[16px] text-white"
            >
              {{ $t('account.wallet.bonus_withdraw.title') }}
            </p>

            <p class="text-[16px] font-bold leading-[24px] text-warning">
              {{ $t('account.wallet.bonus_withdraw.attention') }}
            </p>

            <div
              class="flex flex-col gap-[8px] text-[14px] leading-[23px] text-white"
            >
              <p>
                {{
                  $t(
                    'account.wallet.bonus_withdraw.max_bonus_withdraw_amount',
                    {
                      bonus_multiplier: maxBonusWithdrawalMultiply,
                    },
                  )
                }}
              </p>
              <!-- ENG-863: só o VALOR sai em vermelho (o rótulo segue
                   branco) pra destacar que ele é um limite, não o saldo bônus
                   total exibido no card da carteira. -->
              <p>
                {{ $t('account.wallet.bonus_withdraw.bonus_amount') }}
                <span class="font-bold text-error">
                  {{ showMoneyFormat({ value: withdrawableBonusAmount }) }}
                </span>
              </p>
            </div>

            <p
              class="text-center text-[16px] font-bold leading-[20px] text-white"
            >
              {{ $t('account.wallet.withdraw_modal.withdraw_alert') }}
            </p>

            <div class="flex gap-[14px]">
              <button
                class="flex h-[40px] flex-1 items-center justify-center rounded-[12px] bg-v2-success text-[14px] font-semibold uppercase text-white"
                type="button"
                @click="requestBonusWithdraw"
              >
                {{ $t('account.wallet.withdraw_modal.confirm') }}
              </button>

              <!-- vermelho do Figma (colors/red/600, #dc2626) não bate com
                   nenhum tom da escala --v2-danger (#f31260) — mesma
                   convenção de hex direto usada pro #d4d9e0/#9ba6be em
                   BonusRules.vue. -->
              <button
                class="flex h-[40px] flex-1 items-center justify-center rounded-[12px] bg-[#dc2626] text-[14px] font-semibold uppercase text-white"
                type="button"
                @click="closeModal"
              >
                {{ $t('account.wallet.withdraw_modal.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` no app inteiro — animação de entrada via
// @keyframes/animation (mesma estratégia do BonusRules.vue/LogoutConfirmModal.vue).
.modal-backdrop {
  animation: bonus-withdraw-fade 0.18s ease-out;
}

.modal-card {
  animation: bonus-withdraw-pop 0.2s ease-out;

  @media (max-width: 1023px) {
    animation-name: bonus-withdraw-slide-up;
  }
}

@keyframes bonus-withdraw-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes bonus-withdraw-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes bonus-withdraw-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
